import { apiRequest } from '../../../shared/services/apiClient';
import {
  ChannelAccount,
  ChannelAccountChannel,
  ChannelAccountCredentialStatus,
  ChannelAccountListResponse,
  ChannelReadiness,
  CreateInstagramChannelAccountRequest,
  CreateWhatsAppChannelAccountRequest,
  CreateWhatsAppAppChannelAccountRequest,
  WhatsAppAppSession,
  WhatsAppAppStatusResponse,
  CreateTikTokAppChannelAccountRequest,
  TikTokAppSession,
  TikTokAppStatusResponse,
  UpdateChannelAccountRequest,
  UpdateInstagramChannelAccountCredentialsRequest,
  UpdateWhatsAppChannelAccountCredentialsRequest,
  ChannelAccountLimitsSettingsResponse,
  UpdateChannelAccountLimitsRequest,
  ChannelAccountWhatsAppSettings,
  ChannelAccountInstagramSettings,
  ChannelAccountTelegramSettings,
  ChannelAccountMessengerSettings,
  ChannelAccountTikTokSettings,
  ChannelAccountVoiceSettings,
  CreateTelegramChannelAccountRequest,
  UpdateTelegramChannelAccountCredentialsRequest,
  CreateMessengerChannelAccountRequest,
  UpdateMessengerChannelAccountCredentialsRequest,
  CreateTikTokChannelAccountRequest,
  UpdateTikTokChannelAccountCredentialsRequest,
  CreateVoiceChannelAccountRequest,
  UpdateVoiceChannelAccountCredentialsRequest,
  ChannelAccountSmsSettings,
  CreateSmsChannelAccountRequest,
  UpdateSmsChannelAccountCredentialsRequest,
  ChannelAccountEmailSettings,
  EmailInboundMode,
  EmailInboundState,
  EmailOAuthSettings,
  EmailOAuthProvider,
  EmailOAuthProvidersResponse,
  EmailOAuthStartResponse,
  EmailDnsCheckStatus,
  EmailDnsDiagnostics,
  CreateEmailChannelAccountRequest,
  UpdateEmailChannelAccountCredentialsRequest,
  EmailProviderCatalog,
  EmailSendResult,
  EmailOutboundProvider,
  EmailTransportSecurity,
  CallingConnectionTestResult,
  WhatsAppAppSubscription,
  WhatsAppPhoneNumberRegistration,
  WhatsAppSubscribedApp,
  WhatsAppSubscribedApps,
} from '../types/channelAccount';

const CHANNEL_ACCOUNT_CHANNELS: ChannelAccountChannel[] = [
  'WhatsApp',
  'Instagram',
  'Webchat',
  'Telegram',
  'Messenger',
  'TikTok',
  'Voice',
  'Sms',
  'Email',
];

/**
 * The API serializes MessageChannel as camelCase (`whatsApp`, `tikTok`). Connector UI types use
 * PascalCase (`WhatsApp`, `TikTok`). Without this, a healthy WhatsApp account is stored under the
 * wrong key and conversations warn that the channel is not set up.
 */
export const normalizeChannelAccountChannel = (value: unknown): ChannelAccountChannel | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const raw = value.trim();
  if (!raw) {
    return null;
  }

  return CHANNEL_ACCOUNT_CHANNELS.find(channel => channel.toLowerCase() === raw.toLowerCase()) ?? null;
};

const pickSingle = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const envelope = payload as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === 'object') {
    return envelope.data;
  }

  return payload;
};

const normalizeCredentialStatus = (value: unknown): ChannelAccountCredentialStatus | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const payload = value as Record<string, unknown>;
  return {
    hasAccessToken: Boolean(payload.hasAccessToken),
    hasWebhookVerifyToken: Boolean(payload.hasWebhookVerifyToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignatureEnabled: Boolean(payload.validateSignatureEnabled),
  };
};

const normalizeAccount = (value: unknown): ChannelAccount => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    id: String(payload.id ?? ''),
    channel: normalizeChannelAccountChannel(payload.channel) ?? 'WhatsApp',
    externalAccountId: String(payload.externalAccountId ?? ''),
    displayName: String(payload.displayName ?? ''),
    isEnabled: Boolean(payload.isEnabled),
    isDefault: Boolean(payload.isDefault),
    createdAtUtc: String(payload.createdAtUtc ?? ''),
    updatedAtUtc: String(payload.updatedAtUtc ?? ''),
    credentialStatus: normalizeCredentialStatus(payload.credentialStatus),
    connectionKind: payload.connectionKind === 'app' ? 'app' : payload.connectionKind === 'cloud' ? 'cloud' : null,
  };
};

const normalizeListResponse = (value: unknown): ChannelAccountListResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const limitsRaw = payload.limits && typeof payload.limits === 'object'
    ? payload.limits as Record<string, unknown>
    : {};
  const itemsRaw = Array.isArray(payload.items) ? payload.items : [];

  return {
    items: itemsRaw.map(normalizeAccount),
    limits: {
      maxWhatsApp: Number(limitsRaw.maxWhatsApp ?? 1),
      maxInstagram: Number(limitsRaw.maxInstagram ?? 1),
      maxWebchat: Number(limitsRaw.maxWebchat ?? 5),
      maxTelegram: Number(limitsRaw.maxTelegram ?? 1),
      maxMessenger: Number(limitsRaw.maxMessenger ?? 1),
      maxTikTok: Number(limitsRaw.maxTikTok ?? 1),
      maxVoice: Number(limitsRaw.maxVoice ?? 1),
      maxEmail: Number(limitsRaw.maxEmail ?? 1),
      maxWhatsAppApp: Number(limitsRaw.maxWhatsAppApp ?? 1),
      maxTikTokApp: Number(limitsRaw.maxTikTokApp ?? 1),
      maxTotal: Number(limitsRaw.maxTotal ?? 10),
      usedWhatsApp: Number(limitsRaw.usedWhatsApp ?? 0),
      usedInstagram: Number(limitsRaw.usedInstagram ?? 0),
      usedWebchat: Number(limitsRaw.usedWebchat ?? 0),
      usedTelegram: Number(limitsRaw.usedTelegram ?? 0),
      usedMessenger: Number(limitsRaw.usedMessenger ?? 0),
      usedTikTok: Number(limitsRaw.usedTikTok ?? 0),
      usedVoice: Number(limitsRaw.usedVoice ?? 0),
      usedEmail: Number(limitsRaw.usedEmail ?? 0),
      usedWhatsAppApp: Number(limitsRaw.usedWhatsAppApp ?? 0),
      usedTikTokApp: Number(limitsRaw.usedTikTokApp ?? 0),
      usedTotal: Number(limitsRaw.usedTotal ?? 0),
    },
  };
};

const normalizeWhatsAppSettings = (value: unknown): ChannelAccountWhatsAppSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    cloudApiBaseUrl: String(payload.cloudApiBaseUrl ?? 'https://graph.facebook.com'),
    cloudApiVersion: String(payload.cloudApiVersion ?? 'v25.0'),
    whatsAppAccountId: payload.whatsAppAccountId ? String(payload.whatsAppAccountId) : null,
    cloudPhoneNumberId: String(payload.cloudPhoneNumberId ?? ''),
    phoneNumber: payload.phoneNumber ? String(payload.phoneNumber) : null,
    hasCloudAccessToken: Boolean(payload.hasCloudAccessToken),
    hasCloudWebhookVerifyToken: Boolean(payload.hasCloudWebhookVerifyToken),
    hasCloudAppSecret: Boolean(payload.hasCloudAppSecret),
    validateCloudSignature: Boolean(payload.validateCloudSignature),
  };
};

const normalizeInstagramAccountSettings = (value: unknown): ChannelAccountInstagramSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://graph.instagram.com'),
    apiVersion: String(payload.apiVersion ?? 'v25.0'),
    instagramBusinessAccountId: String(payload.instagramBusinessAccountId ?? ''),
    instagramUsername: payload.instagramUsername ? String(payload.instagramUsername) : null,
    defaultCommentReplyMode: String(payload.defaultCommentReplyMode ?? 'public_comment'),
    hasPageAccessToken: Boolean(payload.hasPageAccessToken),
  };
};

const normalizeTelegramAccountSettings = (value: unknown): ChannelAccountTelegramSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://api.telegram.org'),
    hasBotToken: Boolean(payload.hasBotToken),
    hasWebhookSecretToken: Boolean(payload.hasWebhookSecretToken),
  };
};

const normalizeMessengerAccountSettings = (value: unknown): ChannelAccountMessengerSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://graph.facebook.com'),
    apiVersion: String(payload.apiVersion ?? 'v25.0'),
    pageId: String(payload.pageId ?? ''),
    hasPageAccessToken: Boolean(payload.hasPageAccessToken),
    hasWebhookVerifyToken: Boolean(payload.hasWebhookVerifyToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignature: Boolean(payload.validateSignature),
  };
};

const normalizeTikTokAccountSettings = (value: unknown): ChannelAccountTikTokSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiBaseUrl: String(payload.apiBaseUrl ?? 'https://business-api.tiktok.com/open_api/v1.3'),
    businessOpenId: String(payload.businessOpenId ?? ''),
    appId: payload.appId ? String(payload.appId) : null,
    hasAccessToken: Boolean(payload.hasAccessToken),
    hasAppSecret: Boolean(payload.hasAppSecret),
    validateSignature: Boolean(payload.validateSignature),
  };
};

export const getWhatsAppChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountWhatsAppSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/whatsapp-settings`));
  return normalizeWhatsAppSettings(payload);
};

export const getInstagramChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountInstagramSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/instagram-settings`));
  return normalizeInstagramAccountSettings(payload);
};

export const getTelegramChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountTelegramSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/telegram-settings`));
  return normalizeTelegramAccountSettings(payload);
};

export const getMessengerChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountMessengerSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/messenger-settings`));
  return normalizeMessengerAccountSettings(payload);
};

export const getTikTokChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountTikTokSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/tiktok-settings`));
  return normalizeTikTokAccountSettings(payload);
};

export const listChannelAccounts = async (
  channel?: ChannelAccountChannel,
  connectionKind?: 'cloud' | 'app',
): Promise<ChannelAccountListResponse> => {
  const params = new URLSearchParams();
  if (channel) {
    params.set('channel', channel);
  }
  if (connectionKind) {
    params.set('connectionKind', connectionKind);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts${query}`));
  return normalizeListResponse(payload);
};

const stripQrDataPrefix = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  const comma = trimmed.indexOf(',');
  if (trimmed.toLowerCase().startsWith('data:') && comma >= 0) {
    return trimmed.slice(comma + 1);
  }

  return trimmed;
};

const normalizeWhatsAppAppSession = (value: unknown): WhatsAppAppSession => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    account: normalizeAccount(payload.account),
    connectionStatus: String(payload.connectionStatus ?? 'connecting'),
    qrCodeBase64: stripQrDataPrefix(typeof payload.qrCodeBase64 === 'string' ? payload.qrCodeBase64 : null),
    linkedPhoneNumber: typeof payload.linkedPhoneNumber === 'string' ? payload.linkedPhoneNumber : null,
    lastQrAtUtc: typeof payload.lastQrAtUtc === 'string' ? payload.lastQrAtUtc : null,
    lastDisconnectReason: typeof payload.lastDisconnectReason === 'string' ? payload.lastDisconnectReason : null,
    evolutionConfigured: Boolean(payload.evolutionConfigured),
  };
};

export const getWhatsAppAppStatus = async (): Promise<WhatsAppAppStatusResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/whatsapp-app/status'));
  const data = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return { configured: Boolean(data.configured) };
};

export const createWhatsAppAppChannelAccount = async (
  request: CreateWhatsAppAppChannelAccountRequest,
): Promise<WhatsAppAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/whatsapp-app', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeWhatsAppAppSession(payload);
};

export const getWhatsAppAppSession = async (channelAccountId: string): Promise<WhatsAppAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/whatsapp-app/session`));
  return normalizeWhatsAppAppSession(payload);
};

export const refreshWhatsAppAppQr = async (channelAccountId: string): Promise<WhatsAppAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/whatsapp-app/qr`, {
    method: 'POST',
  }));
  return normalizeWhatsAppAppSession(payload);
};

export const logoutWhatsAppApp = async (channelAccountId: string): Promise<WhatsAppAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/whatsapp-app/logout`, {
    method: 'POST',
  }));
  return normalizeWhatsAppAppSession(payload);
};

const normalizeTikTokAppSession = (value: unknown): TikTokAppSession => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    account: normalizeAccount(payload.account),
    connectionStatus: String(payload.connectionStatus ?? 'connecting'),
    padCode: String(payload.padCode ?? ''),
    deviceSecret: String(payload.deviceSecret ?? ''),
    ingestUrl: String(payload.ingestUrl ?? ''),
    lastHeartbeatAtUtc: typeof payload.lastHeartbeatAtUtc === 'string' ? payload.lastHeartbeatAtUtc : null,
    lastEventAtUtc: typeof payload.lastEventAtUtc === 'string' ? payload.lastEventAtUtc : null,
    watcherSource: typeof payload.watcherSource === 'string' ? payload.watcherSource : null,
  };
};

export const getTikTokAppStatus = async (): Promise<TikTokAppStatusResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/tiktok-app/status'));
  const data = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    configured: Boolean(data.configured),
    recommendedSku: typeof data.recommendedSku === 'string' ? data.recommendedSku : undefined,
    monthlyPriceUsd: typeof data.monthlyPriceUsd === 'number' ? data.monthlyPriceUsd : undefined,
  };
};

export const createTikTokAppChannelAccount = async (
  request: CreateTikTokAppChannelAccountRequest,
): Promise<TikTokAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/tiktok-app', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeTikTokAppSession(payload);
};

export const getTikTokAppSession = async (channelAccountId: string): Promise<TikTokAppSession> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/tiktok-app/session`));
  return normalizeTikTokAppSession(payload);
};

/**
 * Unlike the endpoints above this one needs no connectors permission, so agents working a
 * conversation can be told their channel is not set up. It returns booleans only, never settings.
 */
export const getChannelReadiness = async (): Promise<ChannelReadiness> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-readiness'));
  const items = payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>).items
    : null;

  const readiness: ChannelReadiness = {};
  if (Array.isArray(items)) {
    items.forEach(item => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const entry = item as Record<string, unknown>;
      const channel = normalizeChannelAccountChannel(entry.channel);
      if (channel) {
        readiness[channel] = Boolean(entry.isConfigured);
      }
    });
  }

  return readiness;
};

const normalizeLimits = (value: unknown) => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    maxWhatsApp: Number(payload.maxWhatsApp ?? 1),
    maxInstagram: Number(payload.maxInstagram ?? 1),
    maxWebchat: Number(payload.maxWebchat ?? 5),
    maxTelegram: Number(payload.maxTelegram ?? 1),
    maxMessenger: Number(payload.maxMessenger ?? 1),
    maxTikTok: Number(payload.maxTikTok ?? 1),
    maxVoice: Number(payload.maxVoice ?? 1),
    maxEmail: Number(payload.maxEmail ?? 1),
    maxWhatsAppApp: Number(payload.maxWhatsAppApp ?? 1),
    maxTikTokApp: Number(payload.maxTikTokApp ?? 1),
    maxTotal: Number(payload.maxTotal ?? 10),
    usedWhatsApp: Number(payload.usedWhatsApp ?? 0),
    usedInstagram: Number(payload.usedInstagram ?? 0),
    usedWebchat: Number(payload.usedWebchat ?? 0),
    usedTelegram: Number(payload.usedTelegram ?? 0),
    usedMessenger: Number(payload.usedMessenger ?? 0),
    usedTikTok: Number(payload.usedTikTok ?? 0),
    usedVoice: Number(payload.usedVoice ?? 0),
    usedEmail: Number(payload.usedEmail ?? 0),
    usedWhatsAppApp: Number(payload.usedWhatsAppApp ?? 0),
    usedTikTokApp: Number(payload.usedTikTokApp ?? 0),
    usedTotal: Number(payload.usedTotal ?? 0),
  };
};

const normalizeLimitsSettingsResponse = (value: unknown): ChannelAccountLimitsSettingsResponse => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    effective: normalizeLimits(payload.effective),
    defaults: normalizeLimits(payload.defaults),
    hasOverrides: Boolean(payload.hasOverrides),
  };
};

export const getChannelAccountLimitsSettings = async (): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits'));
  return normalizeLimitsSettingsResponse(payload);
};

export const updateChannelAccountLimits = async (
  request: UpdateChannelAccountLimitsRequest,
): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits', {
    method: 'PUT',
    body: JSON.stringify(request),
  }));
  return normalizeLimitsSettingsResponse(payload);
};

export const resetChannelAccountLimits = async (): Promise<ChannelAccountLimitsSettingsResponse> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/limits', {
    method: 'DELETE',
  }));
  return normalizeLimitsSettingsResponse(payload);
};

export const updateChannelAccount = async (
  channelAccountId: string,
  request: UpdateChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const sendWhatsAppChannelAccountTestMessage = async (
  channelAccountId: string,
  request: {
    to: string;
    body: string;
    type?: 'text';
    previewUrl?: boolean;
  },
): Promise<{ messageSid: string; to: string; status: string; sentAt: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/test-send`,
    {
      method: 'POST',
      body: JSON.stringify({
        type: request.type ?? 'text',
        to: request.to,
        body: request.body,
        ...(request.previewUrl !== undefined ? { previewUrl: request.previewUrl } : {}),
      }),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    messageSid: String(result.messageSid ?? ''),
    to: String(result.to ?? request.to),
    status: String(result.status ?? ''),
    sentAt: String(result.sentAt ?? ''),
  };
};

export const sendInstagramChannelAccountTestMessage = async (
  channelAccountId: string,
  request: {
    recipientId?: string;
    commentId?: string;
    text: string;
  },
): Promise<{ recipientId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/instagram/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    recipientId: String(result.recipientId ?? request.recipientId ?? ''),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

export const createWhatsAppChannelAccount = async (
  request: CreateWhatsAppChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/whatsapp', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateWhatsAppChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateWhatsAppChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

const normalizeWhatsAppAppSubscription = (value: unknown): WhatsAppAppSubscription => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    whatsAppAccountId: String(payload.whatsAppAccountId ?? ''),
    success: Boolean(payload.success),
    updatedAtUtc: String(payload.updatedAtUtc ?? ''),
  };
};

const normalizeWhatsAppSubscribedApp = (value: unknown): WhatsAppSubscribedApp => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    appId: payload.appId ? String(payload.appId) : null,
    appName: payload.appName ? String(payload.appName) : null,
    appLink: payload.appLink ? String(payload.appLink) : null,
  };
};

export const registerWhatsAppPhoneNumber = async (
  channelAccountId: string,
  request: { pin: string; dataLocalizationRegion?: string },
): Promise<WhatsAppPhoneNumberRegistration> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/register-number`,
    {
      method: 'POST',
      body: JSON.stringify({
        pin: request.pin,
        ...(request.dataLocalizationRegion ? { dataLocalizationRegion: request.dataLocalizationRegion } : {}),
      }),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    cloudPhoneNumberId: String(result.cloudPhoneNumberId ?? ''),
    success: Boolean(result.success),
    registeredAtUtc: String(result.registeredAtUtc ?? ''),
  };
};

export const subscribeWhatsAppApp = async (
  channelAccountId: string,
): Promise<WhatsAppAppSubscription> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribe-app`,
    { method: 'POST' },
  ));
  return normalizeWhatsAppAppSubscription(payload);
};

export const unsubscribeWhatsAppApp = async (
  channelAccountId: string,
): Promise<WhatsAppAppSubscription> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribed-apps`,
    { method: 'DELETE' },
  ));
  return normalizeWhatsAppAppSubscription(payload);
};

export const getWhatsAppSubscribedApps = async (
  channelAccountId: string,
): Promise<WhatsAppSubscribedApps> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/whatsapp/subscribed-apps`,
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const itemsRaw = Array.isArray(result.items) ? result.items : [];
  return {
    whatsAppAccountId: String(result.whatsAppAccountId ?? ''),
    items: itemsRaw.map(normalizeWhatsAppSubscribedApp),
    count: Number(result.count ?? itemsRaw.length),
    retrievedAtUtc: String(result.retrievedAtUtc ?? ''),
  };
};

export const createInstagramChannelAccount = async (
  request: CreateInstagramChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/instagram', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateInstagramChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateInstagramChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/instagram-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const createTelegramChannelAccount = async (
  request: CreateTelegramChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/telegram', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateTelegramChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateTelegramChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/telegram-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendTelegramChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { chatId: string; text: string },
): Promise<{ chatId: string; messageId: string; sentAt: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/telegram/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    chatId: String(result.chatId ?? request.chatId),
    messageId: String(result.messageId ?? ''),
    sentAt: String(result.sentAt ?? ''),
  };
};

export const createMessengerChannelAccount = async (
  request: CreateMessengerChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/messenger', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateMessengerChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateMessengerChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/messenger-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendMessengerChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { recipientId: string; text: string },
): Promise<{ recipientId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/messenger/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    recipientId: String(result.recipientId ?? request.recipientId),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

export const createTikTokChannelAccount = async (
  request: CreateTikTokChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/tiktok', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateTikTokChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateTikTokChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/tiktok-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const sendTikTokChannelAccountTestMessage = async (
  channelAccountId: string,
  request: { conversationId: string; text: string },
): Promise<{ conversationId: string; messageId: string; sentAtUtc: string }> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/tiktok/test-send`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  ));

  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    conversationId: String(result.conversationId ?? request.conversationId),
    messageId: String(result.messageId ?? ''),
    sentAtUtc: String(result.sentAtUtc ?? ''),
  };
};

const normalizeVoiceAccountSettings = (value: unknown): ChannelAccountVoiceSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const voiceAgentRaw = payload.voiceAgent && typeof payload.voiceAgent === 'object'
    ? payload.voiceAgent as Record<string, unknown>
    : null;
  return {
    serverUrl: String(payload.serverUrl ?? ''),
    hasClientId: Boolean(payload.hasClientId),
    hasClientSecret: Boolean(payload.hasClientSecret),
    inboundEnabled: payload.inboundEnabled !== false,
    outboundEnabled: payload.outboundEnabled !== false,
    defaultCallerId: payload.defaultCallerId ? String(payload.defaultCallerId) : null,
    provider: String(payload.provider ?? 'ThreeCx'),
    voiceAgent: voiceAgentRaw ? {
      enabled: Boolean(voiceAgentRaw.enabled),
      answerMode: String(voiceAgentRaw.answerMode ?? 'Off'),
      answeredNumbers: voiceAgentRaw.answeredNumbers ? String(voiceAgentRaw.answeredNumbers) : null,
      handoffTarget: voiceAgentRaw.handoffTarget ? String(voiceAgentRaw.handoffTarget) : null,
      greeting: voiceAgentRaw.greeting ? String(voiceAgentRaw.greeting) : null,
      businessHoursStart: voiceAgentRaw.businessHoursStart ? String(voiceAgentRaw.businessHoursStart) : null,
      businessHoursEnd: voiceAgentRaw.businessHoursEnd ? String(voiceAgentRaw.businessHoursEnd) : null,
      businessHoursTimeZone: voiceAgentRaw.businessHoursTimeZone ? String(voiceAgentRaw.businessHoursTimeZone) : null,
      businessHoursWeekdays: voiceAgentRaw.businessHoursWeekdays ? String(voiceAgentRaw.businessHoursWeekdays) : null,
      hasTwilioAccountSid: Boolean(voiceAgentRaw.hasTwilioAccountSid),
      hasTwilioAuthToken: Boolean(voiceAgentRaw.hasTwilioAuthToken),
      twilioFromNumber: voiceAgentRaw.twilioFromNumber ? String(voiceAgentRaw.twilioFromNumber) : null,
      incomingWebhookUrl: voiceAgentRaw.incomingWebhookUrl ? String(voiceAgentRaw.incomingWebhookUrl) : null,
      statusCallbackUrl: voiceAgentRaw.statusCallbackUrl ? String(voiceAgentRaw.statusCallbackUrl) : null,
    } : null,
  };
};

export const getVoiceChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountVoiceSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/voice-settings`));
  return normalizeVoiceAccountSettings(payload);
};

export const createVoiceChannelAccount = async (
  request: CreateVoiceChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/voice', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateVoiceChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateVoiceChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/voice-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const testVoiceChannelAccountConnection = async (
  channelAccountId: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/voice/test-connection`,
    { method: 'POST' },
  ));
  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    succeeded: Boolean(result.succeeded),
    message: result.message ? String(result.message) : null,
    testedAtUtc: String(result.testedAtUtc ?? ''),
  };
};

const normalizeSmsAccountSettings = (value: unknown): ChannelAccountSmsSettings => {
  const payload = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    apiUrl: String(payload.apiUrl ?? ''),
    httpMethod: String(payload.httpMethod ?? 'POST'),
    contentType: String(payload.contentType ?? 'application/json'),
    bodyTemplate: payload.bodyTemplate ? String(payload.bodyTemplate) : null,
    authHeaderName: payload.authHeaderName ? String(payload.authHeaderName) : null,
    hasAuthHeaderValue: Boolean(payload.hasAuthHeaderValue),
    basicAuthUsername: payload.basicAuthUsername ? String(payload.basicAuthUsername) : null,
    hasBasicAuthPassword: Boolean(payload.hasBasicAuthPassword),
    senderName: payload.senderName ? String(payload.senderName) : null,
    successContains: payload.successContains ? String(payload.successContains) : null,
    outboundEnabled: payload.outboundEnabled !== false,
  };
};

export const getSmsChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountSmsSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/sms-settings`));
  return normalizeSmsAccountSettings(payload);
};

export const createSmsChannelAccount = async (
  request: CreateSmsChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/sms', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateSmsChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateSmsChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/sms-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

export const testSmsChannelAccountConnection = async (
  channelAccountId: string,
  phoneNumber: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/sms/test-connection?phoneNumber=${encodeURIComponent(phoneNumber)}`,
    { method: 'POST' },
  ));
  const result = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  return {
    succeeded: Boolean(result.succeeded),
    message: result.message ? String(result.message) : null,
    testedAtUtc: String(result.testedAtUtc ?? ''),
  };
};

// ---- Email ----

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? value as Record<string, unknown> : {};

const optionalString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(item => String(item)).filter(item => item.trim().length > 0) : [];

const normalizeTransportSecurity = (value: unknown, fallback: EmailTransportSecurity): EmailTransportSecurity => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'none' || normalized === 'starttls' || normalized === 'ssl' ? normalized : fallback;
};

const normalizeEmailOutboundProvider = (value: unknown): EmailOutboundProvider => {
  const normalized = String(value ?? '').trim().toLowerCase();
  switch (normalized) {
    case 'sendgrid':
    case 'mailgun':
    case 'postmark':
    case 'resend':
      return normalized;
    default:
      return 'smtp';
  }
};

const normalizeEmailAccountSettings = (value: unknown): ChannelAccountEmailSettings => {
  const payload = asRecord(value);
  const smtp = asRecord(payload.smtp);
  const api = asRecord(payload.api);
  const imap = asRecord(payload.imap);
  const behaviour = asRecord(payload.behaviour);
  const filters = asRecord(payload.filters);
  const replyFormat = String(behaviour.replyFormat ?? 'html').toLowerCase() === 'text' ? 'text' : 'html';
  const rawInboundMode = String(payload.inboundMode ?? 'none').toLowerCase();
  const inboundMode: EmailInboundMode = rawInboundMode === 'imap' || rawInboundMode === 'webhook' ? rawInboundMode : 'none';

  return {
    address: String(payload.address ?? ''),
    fromName: optionalString(payload.fromName),
    replyTo: optionalString(payload.replyTo),
    bccCopyAddress: optionalString(payload.bccCopyAddress),
    outboundProvider: normalizeEmailOutboundProvider(payload.outboundProvider),
    outboundEnabled: payload.outboundEnabled !== false,
    smtp: {
      host: String(smtp.host ?? ''),
      port: Number(smtp.port ?? 587),
      security: normalizeTransportSecurity(smtp.security, 'starttls'),
      username: optionalString(smtp.username),
      hasPassword: Boolean(smtp.hasPassword),
      timeoutSeconds: Number(smtp.timeoutSeconds ?? 30),
    },
    api: {
      hasApiKey: Boolean(api.hasApiKey),
      baseUrl: optionalString(api.baseUrl),
      domain: optionalString(api.domain),
    },
    inboundMode,
    inboundEnabled: Boolean(payload.inboundEnabled),
    imap: {
      host: String(imap.host ?? ''),
      port: Number(imap.port ?? 993),
      security: normalizeTransportSecurity(imap.security, 'ssl'),
      username: optionalString(imap.username),
      hasPassword: Boolean(imap.hasPassword),
      folder: String(imap.folder ?? 'INBOX'),
      processedFolder: optionalString(imap.processedFolder),
      pollIntervalSeconds: Number(imap.pollIntervalSeconds ?? 60),
      markAsRead: imap.markAsRead !== false,
    },
    hasInboundWebhookSecret: Boolean(payload.hasInboundWebhookSecret),
    behaviour: {
      replyFormat,
      subjectPrefix: typeof behaviour.subjectPrefix === 'string' ? behaviour.subjectPrefix : null,
      defaultSubject: optionalString(behaviour.defaultSubject),
      signature: optionalString(behaviour.signature),
      quoteOriginal: Boolean(behaviour.quoteOriginal),
      threadIdleDays: Number(behaviour.threadIdleDays ?? 30),
      maxAttachmentSizeMb: Number(behaviour.maxAttachmentSizeMb ?? 10),
      useBrandLayout: behaviour.useBrandLayout !== false,
      layoutId: optionalString(behaviour.layoutId),
      useAgentSignatures: behaviour.useAgentSignatures !== false,
    },
    filters: {
      allowedSenderDomains: stringList(filters.allowedSenderDomains),
      blockedSenderDomains: stringList(filters.blockedSenderDomains),
      ignoreAutoGenerated: filters.ignoreAutoGenerated !== false,
      ignoreSubjectPattern: optionalString(filters.ignoreSubjectPattern),
    },
    isOutboundConfigured: Boolean(payload.isOutboundConfigured),
    isInboundConfigured: Boolean(payload.isInboundConfigured),
    inboundState: normalizeEmailInboundState(payload.inboundState),
    oauth: normalizeEmailOAuthSettings(payload.oauth),
  };
};

const normalizeEmailOAuthProvider = (value: unknown): EmailOAuthProvider | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'google' || normalized === 'microsoft' ? normalized : null;
};

const normalizeEmailOAuthSettings = (value: unknown): EmailOAuthSettings => {
  const oauth = asRecord(value);
  return {
    provider: normalizeEmailOAuthProvider(oauth.provider),
    isConnected: Boolean(oauth.isConnected),
    account: optionalString(oauth.account),
    connectedAtUtc: optionalString(oauth.connectedAtUtc),
    hasClientId: Boolean(oauth.hasClientId),
    hasClientSecret: Boolean(oauth.hasClientSecret),
    clientId: optionalString(oauth.clientId),
    directoryTenant: optionalString(oauth.directoryTenant),
  };
};

const normalizeEmailInboundState = (value: unknown): EmailInboundState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const state = asRecord(value);
  const lastUid = typeof state.lastUid === 'number' ? state.lastUid : null;
  return {
    lastPolledAtUtc: optionalString(state.lastPolledAtUtc),
    lastImportedAtUtc: optionalString(state.lastImportedAtUtc),
    lastError: optionalString(state.lastError),
    lastUid,
  };
};

export const getEmailProviderCatalog = async (): Promise<EmailProviderCatalog> => {
  const payload = asRecord(pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/email/providers')));
  const providers = Array.isArray(payload.providers) ? payload.providers : [];
  const presets = Array.isArray(payload.presets) ? payload.presets : [];
  return {
    providers: providers.map(item => {
      const provider = asRecord(item);
      return {
        id: String(provider.id ?? ''),
        label: String(provider.label ?? provider.id ?? ''),
        kind: String(provider.kind ?? '').toLowerCase() === 'api' ? 'api' : 'smtp',
        requiresDomain: Boolean(provider.requiresDomain),
        defaultBaseUrl: optionalString(provider.defaultBaseUrl),
        docsUrl: optionalString(provider.docsUrl),
      };
    }),
    presets: presets.map(item => {
      const preset = asRecord(item);
      return {
        id: String(preset.id ?? ''),
        label: String(preset.label ?? preset.id ?? ''),
        smtpHost: optionalString(preset.smtpHost),
        smtpPort: preset.smtpPort != null ? Number(preset.smtpPort) : null,
        smtpSecurity: optionalString(preset.smtpSecurity),
        imapHost: optionalString(preset.imapHost),
        imapPort: preset.imapPort != null ? Number(preset.imapPort) : null,
        imapSecurity: optionalString(preset.imapSecurity),
        note: optionalString(preset.note),
      };
    }),
    securityModes: stringList(payload.securityModes),
    inboundModes: stringList(payload.inboundModes),
    replyFormats: stringList(payload.replyFormats),
  };
};

export const getEmailChannelAccountSettings = async (
  channelAccountId: string,
): Promise<ChannelAccountEmailSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(`/api/v1/channel-accounts/${channelAccountId}/email-settings`));
  return normalizeEmailAccountSettings(payload);
};

export const createEmailChannelAccount = async (
  request: CreateEmailChannelAccountRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/email', {
    method: 'POST',
    body: JSON.stringify(request),
  }));
  return normalizeAccount(payload);
};

export const updateEmailChannelAccountCredentials = async (
  channelAccountId: string,
  request: UpdateEmailChannelAccountCredentialsRequest,
): Promise<ChannelAccount> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email-credentials`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
  ));
  return normalizeAccount(payload);
};

const normalizeConnectionTestResult = (payload: unknown): CallingConnectionTestResult => {
  const result = asRecord(payload);
  return {
    succeeded: Boolean(result.succeeded),
    message: result.message ? String(result.message) : null,
    testedAtUtc: String(result.testedAtUtc ?? ''),
  };
};

export const testEmailChannelAccountOutbound = async (
  channelAccountId: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/test-outbound`,
    { method: 'POST' },
  ));
  return normalizeConnectionTestResult(payload);
};

export const testEmailChannelAccountInbound = async (
  channelAccountId: string,
): Promise<CallingConnectionTestResult> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/test-inbound`,
    { method: 'POST' },
  ));
  return normalizeConnectionTestResult(payload);
};

export const sendEmailChannelAccountTest = async (
  channelAccountId: string,
  to: string,
): Promise<EmailSendResult> => {
  const payload = asRecord(pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/test-send?to=${encodeURIComponent(to)}`,
    { method: 'POST' },
  )));
  return {
    succeeded: Boolean(payload.succeeded),
    messageId: optionalString(payload.messageId),
    failureReason: optionalString(payload.failureReason),
  };
};

export const getEmailOAuthProviders = async (): Promise<EmailOAuthProvidersResponse> => {
  const payload = asRecord(pickSingle(await apiRequest<unknown>('/api/v1/channel-accounts/email/oauth/providers')));
  const providers = Array.isArray(payload.providers) ? payload.providers : [];
  return {
    redirectUri: String(payload.redirectUri ?? ''),
    providers: providers.flatMap(item => {
      const provider = asRecord(item);
      const id = normalizeEmailOAuthProvider(provider.id);
      return id
        ? [{
          id,
          label: String(provider.label ?? id),
          platformAppConfigured: Boolean(provider.platformAppConfigured),
          scopes: stringList(provider.scopes),
        }]
        : [];
    }),
  };
};

export const startEmailOAuth = async (
  channelAccountId: string,
  provider: EmailOAuthProvider,
): Promise<EmailOAuthStartResponse> => {
  const payload = asRecord(pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/oauth/start`,
    { method: 'POST', body: JSON.stringify({ provider }) },
  )));
  return {
    authorizeUrl: String(payload.authorizeUrl ?? ''),
    redirectUri: String(payload.redirectUri ?? ''),
  };
};

export const disconnectEmailOAuth = async (
  channelAccountId: string,
): Promise<ChannelAccountEmailSettings> => {
  const payload = pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/oauth/disconnect`,
    { method: 'POST' },
  ));
  return normalizeEmailAccountSettings(payload);
};

const normalizeDnsCheckStatus = (value: unknown): EmailDnsCheckStatus => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'pass' || normalized === 'warn' || normalized === 'fail' ? normalized : 'unknown';
};

export const checkEmailChannelAccountDns = async (
  channelAccountId: string,
  dkimSelector?: string,
): Promise<EmailDnsDiagnostics> => {
  const query = dkimSelector && dkimSelector.trim() ? `?dkimSelector=${encodeURIComponent(dkimSelector.trim())}` : '';
  const payload = asRecord(pickSingle(await apiRequest<unknown>(
    `/api/v1/channel-accounts/${channelAccountId}/email/dns-check${query}`,
  )));
  const checks = Array.isArray(payload.checks) ? payload.checks : [];
  return {
    domain: String(payload.domain ?? ''),
    outboundProvider: String(payload.outboundProvider ?? ''),
    checkedAtUtc: String(payload.checkedAtUtc ?? ''),
    overallStatus: normalizeDnsCheckStatus(payload.overallStatus),
    checks: checks.map(item => {
      const check = asRecord(item);
      return {
        id: String(check.id ?? ''),
        status: normalizeDnsCheckStatus(check.status),
        summary: String(check.summary ?? ''),
        detail: optionalString(check.detail),
        records: stringList(check.records),
        recommendation: optionalString(check.recommendation),
      };
    }),
  };
};
